import { useCallback } from 'react';
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import logger from '../utils/logger';

const db = getFirestore();

const useEmojiReactions = (setSongs) => {
  const addEmojiReaction = useCallback(async (songId, emoji, userId) => {
    try {
      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();
      if (!songData) throw new Error('Song not found');

      const existingReaction = songData.emojiReactions?.find(
        (reaction) => reaction.userId === userId
      );

      const updatedEmojiReactions = existingReaction
        ? songData.emojiReactions.map((reaction) =>
            reaction.userId === userId ? { ...reaction, emoji } : reaction
          )
        : [
            ...(songData.emojiReactions || []),
            { userId, emoji, timestamp: Timestamp.now() },
          ];

      await updateDoc(songRef, { emojiReactions: updatedEmojiReactions });

      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, emojiReactions: updatedEmojiReactions } : song
        )
      );

      try {
        await httpsCallable(functions, 'emojiReactionNotification')({
          songId,
          emoji,
          songData: {
            URL: songData.URL,
            songName: songData.songName,
            songArtist: songData.songArtist,
            thumbnailURL: songData.thumbnailURL,
          },
          isRemoving: false,
        });
      } catch (notificationError) {
        logger.error('Failed to send emoji reaction notification:', notificationError);
      }

      logger.info(`Emoji reaction added successfully: ${emoji} to song ${songId}`);
      return true;
    } catch (error) {
      logger.error('Error adding emoji reaction:', error);
      throw error;
    }
  }, [setSongs]);

  const removeEmojiReaction = useCallback(async (songId, userId) => {
    try {
      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();
      if (!songData) throw new Error('Song not found');

      const updatedEmojiReactions =
        songData.emojiReactions?.filter((reaction) => reaction.userId !== userId) || [];

      await updateDoc(songRef, { emojiReactions: updatedEmojiReactions });

      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, emojiReactions: updatedEmojiReactions } : song
        )
      );

      try {
        await httpsCallable(functions, 'emojiReactionNotification')({
          songId,
          emoji: '',
          songData: {
            URL: songData.URL,
            songName: songData.songName,
            songArtist: songData.songArtist,
            thumbnailURL: songData.thumbnailURL,
          },
          isRemoving: true,
        });
      } catch (notificationError) {
        logger.error('Failed to send emoji reaction removal notification:', notificationError);
      }

      logger.info(`Emoji reaction removed successfully from song ${songId}`);
      return true;
    } catch (error) {
      logger.error('Error removing emoji reaction:', error);
      throw error;
    }
  }, [setSongs]);

  const incrementPlayCount = useCallback(async (songId) => {
    try {
      await updateDoc(doc(db, 'songs', songId), { playCount: increment(1) });
      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId
            ? { ...song, playCount: (song.playCount || 0) + 1 }
            : song
        )
      );
    } catch (error) {
      logger.error('Error incrementing play count:', error);
    }
  }, [setSongs]);

  return { addEmojiReaction, removeEmojiReaction, incrementPlayCount };
};

export default useEmojiReactions;
